// Uploadt een video in delen naar de hervatbare sessie van de bucket. Na elk deel en na elke
// onderbreking vraagt de pagina de backend hoeveel de bucket heeft, en gaat vanaf daar verder: een
// onderbreking kost alleen het deel dat onderweg was. Lukt het na een paar pogingen niet, dan geeft
// de upload op; opnieuw starten met hetzelfde bestand gaat verder in hetzelfde record.

import axios from 'axios';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;
// Een veelvoud van 256 KiB, zoals de bucket vraagt; Google raadt minstens 8 MiB aan.
export const DEEL_BYTES = 8 * 1024 * 1024;
// Een verbinding die vastloopt (een apparaat in slaap) geeft geen fout; zonder voortgang zo lang
// geldt het deel als afgebroken.
const STIL_MS = 60 * 1000;
const WACHTTIJDEN_MS = [1000, 2000, 4000, 8000, 15000, 30000, 30000, 30000];

function wacht(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function stuurDeel(url, bestand, van, tot, mime, opVoortgang, xhrRef) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    if (xhrRef) xhrRef.current = xhr;
    let stil = setTimeout(() => xhr.abort(), STIL_MS);
    xhr.onloadend = () => clearTimeout(stil);
    xhr.upload.onprogress = (e) => {
      clearTimeout(stil);
      stil = setTimeout(() => xhr.abort(), STIL_MS);
      opVoortgang(van + e.loaded);
    };
    // 308: deel binnen, er volgt meer; 200/201: het hele bestand is binnen.
    xhr.onload = () => (xhr.status === 308 || (xhr.status >= 200 && xhr.status < 300)
      ? resolve()
      : reject(new Error(`Upload mislukt (status ${xhr.status})`)));
    xhr.onerror = () => reject(new Error('Verbinding verbroken'));
    xhr.onabort = () => reject(new Error('Upload afgebroken'));
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', mime);
    xhr.setRequestHeader('Content-Range', `bytes ${van}-${tot - 1}/${bestand.size}`);
    xhr.send(bestand.slice(van, tot));
  });
}

// `start` vraagt de backend om de sessie (POST upload-url) en geeft { documentId, upload_url,
// ontvangen_bytes, klaar }. `opVoortgang(bytes)` krijgt hoeveel er binnen is; `opStoring(bool)` of
// de verbinding weg is en de pagina wacht om het opnieuw te proberen.
export async function uploadVideo({ bestand, mime, start, headers, opVoortgang, opStoring = () => {}, xhrRef }) {
  let sessie = await start();
  let ontvangen = sessie.ontvangen_bytes;
  let klaar = sessie.klaar;
  let fouten = 0;
  opVoortgang(ontvangen);
  while (!klaar) {
    try {
      await stuurDeel(sessie.upload_url, bestand, ontvangen, Math.min(ontvangen + DEEL_BYTES, bestand.size), mime, opVoortgang, xhrRef);
    } catch (err) {
      if (fouten >= WACHTTIJDEN_MS.length) throw err;
      opStoring(true);
      await wacht(WACHTTIJDEN_MS[fouten++]);
    }
    // Het volgende deel begint waar de bucket is; tot dat bekend is, blijft de pagina het vragen.
    for (;;) {
      try {
        const res = await axios.post(`${STRAPI_URL}/api/video-analyses/${sessie.documentId}/upload-stand`, {}, { headers });
        klaar = res.data.data.klaar;
        const nu = res.data.data.ontvangen_bytes ?? ontvangen;
        // Wat vooruitgaat, mag weer haperen: de pogingen tellen per vastgelopen stuk.
        if (nu > ontvangen) fouten = 0;
        ontvangen = nu;
        break;
      } catch (err) {
        if (err.response?.status === 410) {
          // Sessie verlopen: een nieuwe, in hetzelfde record.
          sessie = await start();
          ({ ontvangen_bytes: ontvangen, klaar } = sessie);
          break;
        }
        // Een weigering (geen toegang, verificatie verlopen) wordt niet beter van opnieuw vragen.
        if (err.response?.status < 500 || fouten >= WACHTTIJDEN_MS.length) throw err;
        opStoring(true);
        await wacht(WACHTTIJDEN_MS[fouten++]);
      }
    }
    opStoring(false);
    opVoortgang(ontvangen);
  }
  return sessie.documentId;
}

// Wat een poging die niet aankwam, belooft bij opnieuw proberen. Verder waar het stopte kan alleen met
// een sessie die de bucket nog bewaart (de server zegt dat met `hervatbaar`); anders begint het bij nul.
export function opnieuwTekst(hervatbaar) {
  return hervatbaar
    ? 'Kies opnieuw hetzelfde bestand, dan gaat de upload verder waar hij stopte.'
    : 'Kies opnieuw hetzelfde bestand. Het uploaden begint dan opnieuw.';
}

export function nietAangekomenTekst(analyse) {
  return `Je video is niet aangekomen: de upload stopte voordat het hele bestand binnen was. ${opnieuwTekst(analyse.hervatbaar)} Zonder nieuwe poging verdwijnt dit na een week.`;
}
